import { useMemo } from "react";

import type { StreamResource } from "@prequel/import-react";
import { useListStreams } from "@prequel/import-react";
import { Status, StatusLight, type TableRow } from "@prequel/shared-components";

import { useAllDatasets } from "@/api/import/datasets";
import { useAllDestinations } from "@/api/import/destinations";
import { useAllProviders } from "@/api/import/providers";
import ResourceTable from "@/components/ResourceTable";
import Timestamp from "@/components/Timestamp";

const COLUMNS = ["Name", "Provider", "Dataset", "Destination", "Status", "Created"];

const NameCell = ({ name }: { name: string }) => (
  <span className="block max-w-40 truncate" title={name}>
    {name}
  </span>
);

const StreamsTable = () => {
  const { items: datasets } = useAllDatasets();
  const { items: destinations } = useAllDestinations();
  const { items: providers } = useAllProviders();

  const datasetsByID = useMemo(() => new Map(datasets.map((dataset) => [dataset.id, dataset])), [datasets]);
  const destinationsByID = useMemo(
    () => new Map(destinations.map((destination) => [destination.id, destination])),
    [destinations],
  );
  const providersByID = useMemo(() => new Map(providers.map((provider) => [provider.id, provider])), [providers]);

  const rowMapper = (stream: StreamResource): TableRow => {
    const dataset = datasetsByID.get(stream.dataset_id);
    const provider = dataset ? providersByID.get(dataset.provider_id) : undefined;
    const destination = destinationsByID.get(stream.destination_id);

    return {
      id: stream.id,
      to: `/import/streams/${stream.id}`,
      cells: [
        { value: <NameCell name={stream.name} /> },
        { value: provider ? <NameCell name={provider.name} /> : "-" },
        { value: dataset ? <NameCell name={dataset.name} /> : "-" },
        { value: destination ? <NameCell name={destination.name} /> : "-" },
        {
          value: (
            <span className="flex items-center gap-2">
              <StatusLight status={stream.is_enabled ? Status.ACTIVE : Status.INACTIVE} />
              {stream.is_enabled ? "Enabled" : "Disabled"}
            </span>
          ),
        },
        { value: <Timestamp timestamp={stream.created_at} /> },
      ],
    };
  };

  return (
    <ResourceTable<StreamResource>
      columns={COLUMNS}
      useQuery={useListStreams}
      rowMapper={rowMapper}
      emptyMessage="No streams configured."
    />
  );
};

export default StreamsTable;
